import React, { useRef, useImperativeHandle, cloneElement, ReactElement } from "react";
import { useClickAway } from "../../../hooks/useClickAway";

type TClickAwayListenerProps = {
  onClickAway: (ev: MouseEvent | TouchEvent | PointerEvent) => void;
  children: React.ReactElement;
  triggerOn?: "pointerup" | "click";
  disableReactTree?: boolean;
  onEscape?: (ev: KeyboardEvent) => void;
  insideSelectors?: string[];
};

export const ClickAwayListener = React.forwardRef<HTMLElement, TClickAwayListenerProps>(
  ({ onClickAway, children, triggerOn, disableReactTree, onEscape, insideSelectors }, forwardedRef) => {
    const innerRef = useRef<HTMLElement | null>(null);
    const reactTreeEventRef = useRef<boolean | null>(null);
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLElement);

    const captureProp = (triggerOn ?? "pointerup") === "click" ? "onClickCapture" : "onPointerUpCapture";

    const markReactTreeEvent = () => {
      reactTreeEventRef.current = true;
      setTimeout(() => {
        if (reactTreeEventRef.current) {
          reactTreeEventRef.current = null;
        }
      }, 0);
    };

    useClickAway(innerRef as React.RefObject<HTMLElement>, onClickAway, {
      triggerOn,
      disableReactTree,
      reactTreeRef: reactTreeEventRef,
      onEscape,
      insideSelectors
    });

    return cloneElement(children, {
      ref: (node: HTMLElement) => {
        innerRef.current = node;
        const { ref } = children as any;
        if (typeof ref === "function") ref(node);
        else if (ref && "current" in ref) ref.current = node;
      },
      [captureProp]: (e: any) => {
        markReactTreeEvent();
        const orig = (children.props as any)[captureProp];
        if (typeof orig === "function") orig(e);
      }
    } as any);
  }
);
ClickAwayListener.displayName = "ClickAwayListener";
